/**
 * 51.LA V6 自定义事件桥接。
 *
 * 两套命名，勿混用：
 * - 本地埋点 key：点分，如 `login.open`（只给 trackAnalyticsLog / IndexedDB）
 * - 51.LA 事件标识：下划线，如 `login_open`（只给 LA.track / 控制台）
 *
 * 上报路径：本地 key → LA_EVENT_ID_BY_KEY 查表 → LA.track(下划线标识)。
 * 关键转化另经 la-outbox 持久排队 + 定时核验。
 * 禁止把带「.」的本地 key 传给 LA.track。
 *
 * 口径：51 侧重交易 + 资金，并保留控制台已建且有分析价值的浏览/点击类事件。
 * 人级明细仍只在本地 IndexedDB（deviceId / userId / props）。
 *
 * @see https://v6.51.la/
 */

import { enqueueLaOutbox, setupLaOutbox, sweepLaOutbox } from '@/utils/la-outbox';

/**
 * 已在 51.LA 控制台创建完毕的基准日期（本地）。
 * 之后新增事件：先完成本地埋点验证；控制台建好后再写入映射与已注册快照。
 */
export const LA_CONSOLE_REGISTERED_AT = '2026-08-07';

/**
 * 顺序 = 控制台建事件建议顺序。
 * key = 本地点分；value = 51.LA 事件标识（必须全下划线、与控制台一致，禁止带「.」）。
 * @type {Readonly<Record<string, string>>}
 */
export const LA_EVENT_ID_BY_KEY = Object.freeze({
  // 1. 获客 / 福利
  'acquisition.land': 'acquisition_land',
  'invite.land': 'invite_land',
  'welfare.expose': 'welfare_expose',
  'welfare.close': 'welfare_close',

  // 2. 注册 / 登录
  'register.open': 'register_open',
  'auth.prompt': 'auth_prompt',
  'auth.click_open': 'auth_click_open',
  'login.open': 'login_open',
  'auth.email_submit': 'auth_email_submit',
  'auth.code_success': 'auth_code_success',
  'auth.code_fail': 'auth_code_fail',
  'login.success': 'login_success',
  'auth.register': 'auth_register',
  'login.close': 'login_close',
  'login.logout': 'login_logout',
  'auth.google_click': 'auth_google_click',
  'auth.wallet_click': 'auth_wallet_click',
  'auth.email_click': 'auth_email_click',
  'auth.wallet_fail': 'auth_wallet_fail',

  // 3. 试玩金
  'trial_fund.open': 'trial_fund_open',
  'trial_fund.claim': 'trial_fund_claim',
  'trial_fund.exchange': 'trial_fund_exchange',
  'trial_fund.sim_bet': 'trial_fund_sim_bet',

  // 4. 盘口（新手 / 真实）
  'market.open_newbie': 'market_open_newbie',
  'market.open': 'market_open',
  'market.share': 'market_share',
  'market.outcome_click': 'market_outcome_click',
  'market.chart_range': 'market_chart_range',
  'market.tab_switch': 'market_tab_switch',
  'market.comment_tab': 'market_comment_tab',
  'updown.enter': 'updown_enter',

  // 5. 充值漏斗（资金）
  'deposit.open': 'deposit_open',
  'deposit.choose_crypto': 'deposit_choose_crypto',
  'deposit.choose_cash': 'deposit_choose_cash',
  'deposit.choose_connected_wallet': 'deposit_choose_connected_wallet',
  'deposit.select_network': 'deposit_select_network',
  'deposit.select_pay_method': 'deposit_select_pay_method',
  'deposit.address_view': 'deposit_address_view',
  'deposit.copy_address': 'deposit_copy_address',
  'deposit.qr_view': 'deposit_qr_view',
  'deposit.chain_dwell_10s.tron': 'deposit_chain_dwell_10s_tron',
  'deposit.chain_dwell_10s.eth': 'deposit_chain_dwell_10s_eth',
  'deposit.chain_dwell_10s.bsc': 'deposit_chain_dwell_10s_bsc',
  'deposit.chain_dwell_10s.polygon': 'deposit_chain_dwell_10s_polygon',
  'deposit.chain_dwell_10s.arbitrum': 'deposit_chain_dwell_10s_arbitrum',
  'deposit.chain_dwell_10s.solana': 'deposit_chain_dwell_10s_solana',
  'deposit.chain_dwell_10s.base': 'deposit_chain_dwell_10s_base',
  'deposit.pay_now': 'deposit_pay_now',
  'deposit.transfer_submitted': 'deposit_transfer_submitted',
  'deposit.chain_confirmed': 'deposit_chain_confirmed',
  'deposit.pay_confirmed': 'deposit_pay_confirmed',
  'deposit.return': 'deposit_return',
  'deposit.success': 'deposit_success',
  'deposit.success_first': 'deposit_success_first',
  'deposit.recover': 'deposit_recover',
  'deposit.fail': 'deposit_fail',
  'deposit.close': 'deposit_close',

  // 6. 提现（资金）
  'withdraw.open': 'withdraw_open',
  'withdraw.select_network': 'withdraw_select_network',
  'withdraw.confirm_step': 'withdraw_confirm_step',
  'withdraw.amount_preset': 'withdraw_amount_preset',
  'withdraw.submit': 'withdraw_submit',
  'withdraw.success': 'withdraw_success',
  'withdraw.fail': 'withdraw_fail',
  'withdraw.close': 'withdraw_close',

  // 7. 账户 / 积分
  'account.open': 'account_open',
  'account.tab_switch': 'account_tab_switch',
  'account.deposit_click': 'account_deposit_click',
  'account.withdraw_click': 'account_withdraw_click',
  'account.dscoin_click': 'account_dscoin_click',
  'points.open': 'points_open',
  'points.exchange': 'points_exchange',

  // 8. 交易
  'trade.dialog.open': 'trade_dialog_open',
  'trade.side_select': 'trade_side_select',
  'trade.order_type': 'trade_order_type',
  'trade.amount_preset': 'trade_amount_preset',
  'trade.max_click': 'trade_max_click',
  'trade.submit.start': 'trade_submit_start',
  'trade.order_open': 'trade_order_open',
  'trade.submit.success': 'trade_submit_success',
  'trade.success_first': 'trade_success_first',
  'trade.submit.fail': 'trade_submit_fail',
  'trade.submit.abort': 'trade_submit_abort',
  'trade.blocked': 'trade_blocked',
  'trade.dialog.close': 'trade_dialog_close',
  'trade.approve.open': 'trade_approve_open',
  'trade.approve.success': 'trade_approve_success',

  // 9. 有效访问（页面阶段与通用点击仅保留本地日志）
  'visit.engaged_10s': 'visit_engaged_10s',
  'visit.engaged_action': 'visit_engaged_action',

  // 10. 收藏 / 搜索 / 邀请
  'watchlist.toggle': 'watchlist_toggle',
  'search.submit': 'search_submit',
  'search.result_click': 'search_result_click',
  'invite.open': 'invite_open',
  'invite.copy': 'invite_copy',
  'invite.share': 'invite_share',
  'feed.load_more': 'feed_load_more',
  'feed.refresh': 'feed_refresh',

  // 11. 创建市场
  'create_market.open': 'create_market_open',
  'create_market.success': 'create_market_success',
  'create_market.fail': 'create_market_fail',
  'nav.create_click': 'nav_create_click',

  // 12. 频道 / 语言 / 客服 / 助手 / 通知 / 资料 / PWA / 帮助 / 体育
  'channel.switch': 'channel_switch',
  'locale.switch': 'locale_switch',
  'support.open': 'support_open',
  'support.contact.email': 'support_contact_email',
  'support.contact.telegram': 'support_contact_telegram',
  'assistant.open': 'assistant_open',
  'assistant.help_click': 'assistant_help_click',
  'nav.logo_click': 'nav_logo_click',
  'nav.search_open': 'nav_search_open',
  'feed.like': 'feed_like',
  'notification.open': 'notification_open',
  'notification.click': 'notification_click',
  'profile.open': 'profile_open',
  'profile.save': 'profile_save',
  'pwa.install.click': 'pwa_install_click',
  'pwa.install.result': 'pwa_install_result',
  'help.open': 'help_open',
  'help.doc_open': 'help_doc_open',
  'sport.list_enter': 'sport_list_enter',
  'sport.live_enter': 'sport_live_enter',
  'sport.futures_enter': 'sport_futures_enter',
  'crypto.list_enter': 'crypto_list_enter',
  'leagues.list_enter': 'leagues_list_enter',

  // 13. 登录后新手跳转 + 试玩盘口引导
  'onboarding.trial_sim': 'onboarding_trial_sim',
  'onboarding.updown': 'onboarding_updown',
  'onboarding.home': 'onboarding_home',
  'onboarding.guide_start': 'onboarding_guide_start',
  'onboarding.guide_complete': 'onboarding_guide_complete',
  'onboarding.guide_skip': 'onboarding_guide_skip',
});

/**
 * 已在 https://v6.51.la/ 控制台创建的事件标识快照（截至 LA_CONSOLE_REGISTERED_AT）。
 * 用于区分「代码已映射但控制台未建」的后续新增；勿删改历史条目。
 * @type {ReadonlySet<string>}
 */
export const LA_CONSOLE_REGISTERED_EVENT_IDS = Object.freeze(
  new Set([
    'acquisition_land',
    'welfare_expose',
    'welfare_close',
    'register_open',
    'auth_prompt',
    'login_open',
    'auth_email_submit',
    'auth_code_success',
    'auth_code_fail',
    'login_success',
    'auth_register',
    'login_close',
    'login_logout',
    'trial_fund_open',
    'trial_fund_claim',
    'trial_fund_exchange',
    'market_open_newbie',
    'market_open',
    'market_share',
    'deposit_open',
    'deposit_choose_crypto',
    'deposit_choose_cash',
    'deposit_choose_connected_wallet',
    'deposit_pay_now',
    'deposit_transfer_submitted',
    'deposit_pay_confirmed',
    'deposit_return',
    'deposit_success',
    'deposit_fail',
    'deposit_close',
    'withdraw_open',
    'withdraw_submit',
    'withdraw_success',
    'withdraw_fail',
    'withdraw_close',
    'trade_dialog_open',
    'trade_submit_start',
    'trade_submit_success',
    'trade_submit_fail',
    'trade_submit_abort',
    'trade_blocked',
    'trade_dialog_close',
    'trade_approve_open',
    'trade_approve_success',
    'page_enter',
    'page_ready',
    'watchlist_toggle',
    'search_submit',
    'search_result_click',
    'invite_copy',
    'invite_share',
    'create_market_open',
    'create_market_success',
    'create_market_fail',
    'points_exchange',
    'account_dscoin_click',
    'channel_switch',
    'notification_click',
    'profile_save',
    'pwa_install_click',
    'pwa_install_result',
    'help_open',
    'sport_live_enter',
    'sport_futures_enter',
  ])
);

/**
 * @param {string} eventId 51.LA 事件标识（下划线）
 * @returns {boolean}
 */
export function isLaConsoleRegistered(eventId) {
  return LA_CONSOLE_REGISTERED_EVENT_IDS.has(String(eventId || '').trim());
}

/**
 * 映射表里有、但尚未记入「控制台已建」集合的标识（后续新增待建）。
 * @returns {string[]}
 */
export function listLaEventsPendingConsole() {
  const pending = [];
  for (const eventId of Object.values(LA_EVENT_ID_BY_KEY)) {
    if (!LA_CONSOLE_REGISTERED_EVENT_IDS.has(eventId)) pending.push(eventId);
  }
  return pending;
}

/**
 * 将本地点分 key 解析为 51.LA 事件标识（仅下划线）。
 * @param {string} key 本地 key，如 login.open
 * @returns {string} 如 login_open；无效则空串
 */
export function resolveLaEventId(key) {
  const localKey = String(key || '').trim();
  if (!localKey) return '';
  const eventId = LA_EVENT_ID_BY_KEY[localKey];
  if (!eventId) return '';
  // 51.LA 只要下划线标识；映射值若误含「.」则拒绝上报
  if (eventId.includes('.') || !/^[a-z][a-z0-9_]*$/.test(eventId)) return '';
  return eventId;
}

/**
 * 必须进 Outbox 的关键转化（含接口 / 定时器 / 链上轮询等非点击）。
 * 点击类转化也列入，保证 SDK 晚到仍可核验 dispatch。
 * 仅 LA_OUTBOX_KEYS 进入队列；其余已映射低频事件直接 trySend，本地专用事件无映射不上报。
 * @type {ReadonlySet<string>}
 */
export const LA_OUTBOX_KEYS = Object.freeze(
  new Set([
    // 获客 / 访问
    'acquisition.land',
    'invite.land',
    'visit.engaged_10s',
    'visit.engaged_action',
    // 注册 / 登录
    'register.open',
    'auth.click_open',
    'login.open',
    'auth.email_submit',
    'auth.code_success',
    'login.success',
    'auth.register',
    // 试玩金（接口结果）
    'trial_fund.open',
    'trial_fund.claim',
    'trial_fund.exchange',
    // 登录引导 / 盘口引导
    'onboarding.trial_sim',
    'onboarding.updown',
    'onboarding.home',
    'onboarding.guide_start',
    'onboarding.guide_complete',
    'onboarding.guide_skip',
    // 充值漏斗
    'deposit.open',
    'deposit.choose_crypto',
    'deposit.choose_cash',
    'deposit.choose_connected_wallet',
    'deposit.select_network',
    'deposit.select_pay_method',
    'deposit.address_view',
    'deposit.copy_address',
    'deposit.qr_view',
    'deposit.chain_dwell_10s.tron',
    'deposit.chain_dwell_10s.eth',
    'deposit.chain_dwell_10s.bsc',
    'deposit.chain_dwell_10s.polygon',
    'deposit.chain_dwell_10s.arbitrum',
    'deposit.chain_dwell_10s.solana',
    'deposit.chain_dwell_10s.base',
    'deposit.pay_now',
    'deposit.transfer_submitted',
    'deposit.chain_confirmed',
    'deposit.pay_confirmed',
    'deposit.return',
    'deposit.success',
    'deposit.success_first',
    'deposit.recover',
    'deposit.fail',
    // 提现（资金）
    'withdraw.open',
    'withdraw.select_network',
    'withdraw.confirm_step',
    'withdraw.submit',
    'withdraw.success',
    'withdraw.fail',
    // 交易结果（按订单状态）
    'trade.submit.start',
    'trade.order_open',
    'trade.submit.success',
    'trade.success_first',
    'trade.submit.fail',
    'trade.submit.abort',
    // 客服 / 语言（低频但关键；按渠道拆事件）
    'support.open',
    'support.contact.email',
    'support.contact.telegram',
    'locale.switch',
  ])
);

/**
 * @param {string} key
 * @returns {boolean}
 */
export function isLaOutboxKey(key) {
  return LA_OUTBOX_KEYS.has(String(key || '').trim());
}

/**
 * @param {string} eventId
 * @returns {boolean}
 */
export function trySendLaEventId(eventId) {
  const id = String(eventId || '').trim();
  if (!id) return false;
  try {
    if (typeof window === 'undefined') return false;
    const la = window.LA;
    if (!la) return false;
    if (typeof la.track === 'function') {
      la.track(id);
      return true;
    }
    if (typeof la.quick === 'function') {
      la.quick('autoTrackCustom', { name: id, type: id });
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * @param {string} key 本地 analytics key（点分，如 login.open）。
 * @param {{ dedupeKey?: string }} [opts]
 * 内部映射为下划线事件标识后再 LA.track；绝不会把 login.open 传给 51.LA。
 * 关键事件走持久 Outbox + 定时核验（见 la-outbox.js）。
 */
export function trackLaEventByKey(key, opts = {}) {
  const localKey = String(key || '').trim();
  const eventId = resolveLaEventId(localKey);
  if (!eventId) return;

  if (!isLaOutboxKey(localKey)) {
    trySendLaEventId(eventId);
    return;
  }

  const dedupeKey = opts.dedupeKey ? String(opts.dedupeKey).trim() : '';
  enqueueLaOutbox({
    key: localKey,
    eventId,
    ...(dedupeKey ? { dedupeKey } : {}),
  });
  sweepLaOutbox();
}

/**
 * 应用启动时挂载 Outbox sweeper（visibility / 定时重试）。
 */
export function setupLaTrackOutbox() {
  setupLaOutbox({ sendEventId: trySendLaEventId });
}
